import { useCurrentExchangeRate } from 'api/exchange-rate'
import { Collapsable } from 'components/common/Collapsable'
import { Input } from 'components/common/Input'
import { Range } from 'components/common/Range'
import { Separator } from 'components/common/Separator'
import {
  getLowestAskItems,
  NumericMenusMap,
  RefinementListsMap,
} from 'constants/instantsearch'
import { debounce } from 'debounce'
import { useCurrencies } from 'contexts/hooks/useCurrencies'
import useTranslation from 'next-translate/useTranslation'
import { ReactNode, useCallback, useRef, useState } from 'react'
import {
  ToggleRefinement,
  useNumericMenu,
  useRange,
  useRefinementList,
  useToggleRefinement,
} from 'react-instantsearch'
import Skeleton from 'react-loading-skeleton'
import { FacetValue } from 'types'
import { range } from 'utils/arrays'

const ColorsMap = Object.freeze({
  multi: 'url(/files/multi.png)',
  black: '#14142B',
  blue: '#4258D2',
  red: '#C02C23',
  white: '#FFFFFF',
  pink: '#FE67B2',
  yellow: '#F9D650',
  green: '#006650',
  gold: '#E5CB5A',
  grey: '#B7B7B7',
  orange: '#D8772D',
  silver: '#DFE1E2',
  brown: '#775542',
  purple: '#9D04E1',
})

const SizesMap = Object.freeze({
  small: '(under 40cm)',
  medium: '(40 - 100cm)',
  large: '(over 100cm)',
})

const sortedSizeItems = ['Small', 'Medium', 'Large']

const sortedEditionSizeItems = [
  'Open edition',
  '< 250',
  '250 - 500',
  '500 - 1000',
  '> 1000',
]

const Panel = ({
  children,
  title,
  className,
}: {
  children: ReactNode
  title: ReactNode
  className?: string
}) => {
  const [isOpen, setIsOpen] = useState(true)

  return (
    <Collapsable
      title={title}
      iconClassName='!text-cool-500'
      open={isOpen}
      onChange={open => setIsOpen(open)}
    >
      <div className={`w-full ${className}`}>{children}</div>
    </Collapsable>
  )
}

export const List = ({
  items,
  refine,
  search,
  toggleShowMore,
  canShowMore,
  isShowingMore,
  searchablePlaceholder,
  type = 'checkbox',
  skeletonsCount = 0,
}: {
  items: { label: string; value: string; isRefined: boolean }[]
  refine: (value: string) => void
  search?: (query: string) => void
  toggleShowMore?: () => void
  canShowMore?: boolean
  isShowingMore?: boolean
  searchablePlaceholder?: string
  type?: 'radio' | 'checkbox'
  skeletonsCount?: number
}) => {
  return (
    <div className='mt-5'>
      {search && (
        <div className='mb-4'>
          <Input
            type='search'
            onChange={e => search(e.target.value)}
            autoComplete='off'
            autoCorrect='off'
            placeholder={searchablePlaceholder}
          />
        </div>
      )}

      <ul className='flex flex-col space-y-3'>
        {!items.length &&
          Boolean(skeletonsCount) &&
          range(0, skeletonsCount).map(i => (
            <li key={i} className='flex'>
              <Skeleton containerClassName='h-full w-full' />
            </li>
          ))}

        {items.map(({ label, value, isRefined }) => {
          const color = ColorsMap[value.toLowerCase() as keyof typeof ColorsMap]
          const size = SizesMap[value.toLowerCase() as keyof typeof SizesMap]

          const onClick = () => {
            refine(value)
          }

          return (
            <li
              key={value}
              className='flex cursor-pointer items-center justify-between'
              onClick={onClick}
              data-e2e={`artwork-filter-${label.toLowerCase()}`}
            >
              <label
                className='flex cursor-pointer items-center space-x-4'
                onClick={onClick}
              >
                <Input
                  type={type}
                  checked={isRefined}
                  onClick={onClick}
                  className='outline-none'
                />

                <div>
                  <span className='text-sm'>{label}</span>

                  {size && (
                    <span className='text-sm text-cool-500'> {size}</span>
                  )}
                </div>
              </label>

              {color && (
                <div
                  style={{
                    background: color,
                    backgroundPosition: 'center',
                    backgroundSize: 'cover',
                    borderColor:
                      value === 'White' ? 'rgb(217 219 233)' : undefined,
                    borderWidth: value === 'White' ? '1px' : undefined,
                  }}
                  className='h-6 w-6 rounded-full'
                ></div>
              )}
            </li>
          )
        })}
      </ul>

      {canShowMore && toggleShowMore && (
        <button
          type='button'
          className='mt-3 text-sm text-secondary-default'
          onClick={toggleShowMore}
        >
          {isShowingMore ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}

type Props = {
  className?: string
  scrollToArtworks: () => void
}

const releaseDateBounds = { min: 1980, max: new Date().getFullYear() }

export const Filters = ({ className, scrollToArtworks }: Props) => {
  const { t } = useTranslation('artworks')
  const { userCurrency } = useCurrencies()
  const { data: exchange } = useCurrentExchangeRate()

  useToggleRefinement({
    attribute: 'entity',
    on: 'artwork',
    off: 'artwork',
  })

  useToggleRefinement({
    attribute: 'isDeleted',
    on: false,
    off: false,
  })

  const sortItems = useCallback(
    (a: FacetValue, b: FacetValue, sortedArr: string[]) => {
      const idxA = sortedArr.indexOf(a.name) || 0
      const idxB = sortedArr.indexOf(b.name) || 0

      const defaultValue = idxA - idxB

      if (a.isRefined && b.isRefined) return defaultValue

      if (a.isRefined && !b.isRefined) return -1

      if (!a.isRefined && b.isRefined) return 1

      return defaultValue
    },
    []
  )

  const sortSizeItems = useCallback(
    (a: FacetValue, b: FacetValue) => sortItems(a, b, sortedSizeItems),
    [sortItems]
  )

  const sortEditionSizeItems = useCallback(
    (a: FacetValue, b: FacetValue) => sortItems(a, b, sortedEditionSizeItems),
    [sortItems]
  )

  const refineHandler = useCallback(
    (cb: () => void) => {
      cb()
      scrollToArtworks()
    },
    [scrollToArtworks]
  )

  const { items: mediumItems, refine: refineMedium } = useRefinementList({
    attribute: RefinementListsMap.medium.attribute,
  })

  const { canRefine: canRefineBuyNow } = useToggleRefinement({
    attribute: 'buyNowAvailability',
  })

  const { canRefine: canRefineSellNow } = useToggleRefinement({
    attribute: 'sellNowAvailability',
  })

  const { items: priceItems, refine: refinePrice } = useNumericMenu({
    attribute: NumericMenusMap.lowestAsk.attribute,
    items: getLowestAskItems(userCurrency, exchange),
  })

  const galleryRefinement = useRefinementList({
    attribute: 'gallery',
    showMore: true,
    limit: 6,
    sortBy: ['isRefined', 'name'],
  })

  const { items: sizeItems, refine: refineSize } = useRefinementList({
    attribute: 'size',
    sortBy: sortSizeItems,
  })

  const { items: editionSizeItems, refine: refineEditionSize } =
    useRefinementList({
      attribute: 'editionRange',
      sortBy: sortEditionSizeItems,
    })

  const { start: currentReleaseDateRange, refine: refineReleaseDateRange } =
    useRange({
      attribute: 'releaseDateYear',
      ...releaseDateBounds,
    })

  const nationalityRefinement = useRefinementList({
    attribute: 'artists.nationalities',
    showMore: true,
    limit: 6,
    sortBy: ['isRefined', 'name'],
  })

  const { items: colorItems, refine: refineColor } = useRefinementList({
    attribute: 'color',
    limit: 50,
  })

  const refineReleaseDateHandler = useRef(
    debounce((newReleaseDateRange: [number, number]) => {
      refineReleaseDateRange(newReleaseDateRange)
      scrollToArtworks()
    }, 250)
  )

  const classNames = {
    list: 'flex flex-col space-y-3 mt-4',
    label: 'flex cursor-pointer text-sm',
    checkbox: 'mr-3 rounded',
  }

  return (
    <div className={className}>
      <Panel title={t('medium')}>
        <List
          items={mediumItems}
          refine={value => refineHandler(() => refineMedium(value))}
          skeletonsCount={4}
        />
      </Panel>

      <Separator className='group my-6' />

      <Panel title={t('availability')}>
        <ul className={classNames.list}>
          {[
            {
              attribute: 'buyNowAvailability',
              label: 'Buy now',
              canRefine: canRefineBuyNow,
            },
            {
              attribute: 'sellNowAvailability',
              label: 'Sell now',
              canRefine: canRefineSellNow,
            },
          ].map(({ label, attribute, canRefine }) => {
            if (!canRefine) return null

            return (
              <li key={attribute}>
                <ToggleRefinement
                  attribute={attribute}
                  label={label}
                  classNames={{
                    label: classNames.label,
                    checkbox: classNames.checkbox,
                  }}
                  onClick={scrollToArtworks}
                />
              </li>
            )
          })}
        </ul>
      </Panel>

      <Separator className='my-6' />

      <Panel title={t('price')}>
        <List
          items={priceItems}
          refine={value => refineHandler(() => refinePrice(value))}
          type='radio'
        />
      </Panel>

      <Separator className='my-6' />

      <Panel title={t('gallery')}>
        <List
          items={galleryRefinement.items}
          refine={value => refineHandler(() => galleryRefinement.refine(value))}
          search={galleryRefinement.searchForItems}
          canShowMore={galleryRefinement.canToggleShowMore}
          toggleShowMore={galleryRefinement.toggleShowMore}
          isShowingMore={galleryRefinement.isShowingMore}
          searchablePlaceholder='Search name'
          skeletonsCount={6}
        />
      </Panel>

      <Separator className='my-6' />

      <Panel
        title={
          <>
            {t('size')}{' '}
            <span className='ml-1 text-xs text-cool-500'>
              ({t('avg_dimension')})
            </span>
          </>
        }
      >
        <List
          items={sizeItems}
          refine={value => refineHandler(() => refineSize(value))}
          skeletonsCount={3}
        />
      </Panel>

      <Separator className='my-6' />

      <Panel title={t('edition_size')}>
        <List
          items={editionSizeItems}
          refine={value => refineHandler(() => refineEditionSize(value))}
          skeletonsCount={5}
        />
      </Panel>

      <Separator className='my-6' />

      <Panel title={t('release_date')}>
        <Range
          step={1}
          min={releaseDateBounds.min}
          max={releaseDateBounds.max}
          values={{
            min: currentReleaseDateRange[0] || releaseDateBounds.min,
            max: currentReleaseDateRange[1] || releaseDateBounds.max,
          }}
          onChange={({ min, max }) => {
            refineReleaseDateHandler.current([min, max])
          }}
          className='pb-1'
        />
      </Panel>

      <Separator className='my-6' />

      <Panel title={t('artist_nationality')}>
        <List
          items={nationalityRefinement.items}
          refine={value =>
            refineHandler(() => nationalityRefinement.refine(value))
          }
          canShowMore={nationalityRefinement.canToggleShowMore}
          isShowingMore={nationalityRefinement.isShowingMore}
          search={nationalityRefinement.searchForItems}
          searchablePlaceholder='Search nationality'
          toggleShowMore={nationalityRefinement.toggleShowMore}
          skeletonsCount={6}
        />
      </Panel>

      <Separator className='my-6' />

      <Panel title={t('colour')}>
        <List
          items={colorItems}
          refine={value => refineHandler(() => refineColor(value))}
          skeletonsCount={9}
        />
      </Panel>
    </div>
  )
}
